import React, { useEffect, useMemo, useRef, useState } from 'react';
import { trackZoomFactor } from '../trackZoom';
import './IntelArrivalsTrack.css';

const LABEL_COL_PX = 100;
const MARKER_SIZE = 10;

function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatArrivalTip(arrival) {
  if (!arrival) return '';
  const reasons = (arrival.reasons || []).join(', ');
  const flags = [
    arrival.producing ? 'producing' : null,
    arrival.researching ? 'researching' : null,
  ]
    .filter(Boolean)
    .join(', ');
  const parts = [
    formatClock(arrival.real_time),
    arrival.subject_type,
    arrival.method,
    reasons && `reasons: ${reasons}`,
    flags,
  ].filter(Boolean);
  return parts.join(' · ');
}

/**
 * Intel Arrivals lane — markers from /api/replay_facts/…/intel_arrivals.
 * Shared zoom/scroll with Production / Losses Track (D57).
 */
const IntelArrivalsTrack = ({
  apiBaseUrl = null,
  replayId = null,
  player = null,
  duration = 0,
  time = 0,
  onSeek,
  zoomIndex = 0,
  scrollLeft = 0,
  onScrollLeftChange,
  onHoverChange,
}) => {
  const scrollRef = useRef(null);
  const onSeekRef = useRef(onSeek);
  onSeekRef.current = onSeek;
  const onHoverRef = useRef(onHoverChange);
  onHoverRef.current = onHoverChange;
  const [viewportWidth, setViewportWidth] = useState(0);
  const [state, setState] = useState({
    status: 'idle',
    arrivals: [],
    coverage: null,
    error: '',
  });

  useEffect(() => {
    if (!apiBaseUrl || !replayId || !player) {
      setState({ status: 'empty', arrivals: [], coverage: null, error: '' });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', arrivals: [], coverage: null, error: '' });
    const url =
      `${apiBaseUrl}/replay_facts/${encodeURIComponent(replayId)}` +
      `/intel_arrivals?player=${encodeURIComponent(player)}`;
    fetch(url)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setState({
          status: 'ready',
          arrivals: Array.isArray(data?.arrivals) ? data.arrivals : [],
          coverage: data?.coverage || null,
          error: '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: 'error',
          arrivals: [],
          coverage: null,
          error: err.message || 'Failed to load',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, replayId, player]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setViewportWidth(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof scrollLeft !== 'number') return;
    if (Math.abs(el.scrollLeft - scrollLeft) > 1) {
      el.scrollLeft = scrollLeft;
    }
  }, [scrollLeft]);

  const maxT = Math.max(duration || 0, 1);
  const zoom = trackZoomFactor(zoomIndex);
  // Match Losses Track gutter so shared scrollLeft stays aligned (rules/frontend.md).
  const timelineViewport = Math.max(0, viewportWidth - LABEL_COL_PX - 8);
  const fitPxPerSecond = timelineViewport > 0 ? timelineViewport / maxT : 1;
  const timelineWidthPx = Math.max(timelineViewport, maxT * fitPxPerSecond * zoom, 1);
  const innerWidthPx = LABEL_COL_PX + 8 + timelineWidthPx;

  const coverageHint = useMemo(() => {
    const c = state.coverage;
    if (!c) return '';
    if (c.vision_suppressed) return 'vision suppressed';
    return '';
  }, [state.coverage]);

  return (
    <div className="intel-arrivals-track" data-testid="intel-arrivals-track">
      <div className="intel-arrivals-header">
        <h4>Intel Arrivals{player ? ` · ${player}` : ''}</h4>
        {coverageHint ? (
          <span className="intel-arrivals-coverage">{coverageHint}</span>
        ) : null}
      </div>
      {state.status === 'error' ? (
        <div className="intel-arrivals-empty">{state.error}</div>
      ) : state.status === 'empty' || (!apiBaseUrl && !replayId) ? (
        <div className="intel-arrivals-empty">No replay selected.</div>
      ) : (
        <div
          className="intel-arrivals-scroll"
          ref={scrollRef}
          onScroll={(e) => onScrollLeftChange?.(e.currentTarget.scrollLeft)}
        >
          <div
            className="intel-arrivals-inner"
            style={{ width: `${innerWidthPx}px` }}
          >
            <div className="intel-arrivals-row">
              <div className="intel-arrivals-label">Arrivals</div>
              <div
                className="intel-arrivals-lane"
                style={{ width: `${timelineWidthPx}px` }}
              >
                <div
                  className="intel-arrivals-playhead"
                  style={{ left: `${(time / maxT) * timelineWidthPx}px` }}
                />
                {(state.arrivals || []).map((arrival) => {
                  const t = arrival.real_time || 0;
                  const left = (t / maxT) * timelineWidthPx - MARKER_SIZE / 2;
                  const tip = formatArrivalTip(arrival);
                  const key = `${arrival.id || ''}:${arrival.subject_unit_id}:${t}:${arrival.method}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      className="intel-arrival-marker"
                      style={{ left: `${left}px` }}
                      aria-label={tip}
                      title={tip}
                      onMouseEnter={(e) => {
                        onHoverRef.current?.({
                          kind: 'intel_arrival',
                          text: tip,
                          arrival,
                          clientX: e.clientX,
                          clientY: e.clientY,
                        });
                      }}
                      onMouseMove={(e) => {
                        onHoverRef.current?.({
                          kind: 'intel_arrival',
                          text: tip,
                          arrival,
                          clientX: e.clientX,
                          clientY: e.clientY,
                        });
                      }}
                      onMouseLeave={() => onHoverRef.current?.(null)}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSeekRef.current?.(t);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {state.status === 'loading' ? (
        <div className="intel-arrivals-status">Loading…</div>
      ) : null}
    </div>
  );
};

export default IntelArrivalsTrack;
